import { useMemo } from 'react';
import { Check, Sparkles } from 'lucide-react';
import { useTheme } from '../../../../../hooks/useTheme';
import SubStepBreadcrumb from './SubStepBreadcrumb';
import { calcMaxTopics, calcMaxWeightage } from './utils/jdUtils';

// Step 2b — Weightage + difficulty assignment per skill.
// Placed between Focus Area and Requirements Gathering so topic limits
// can be derived from weightage before the user picks topics.

const DIFFICULTY_OPTIONS = [
  { value: 'easy', label: 'Easy', description: 'Foundational concepts' },
  { value: 'medium', label: 'Medium', description: 'Working knowledge' },
  { value: 'hard', label: 'Hard', description: 'Deep expertise' },
];

// fillPct must be normalized to 0–100 relative to the slider's max, not the raw value
const SLIDER_MIN = 10;

function getSliderBackground(value, max, theme) {
  const fillPct =
    max > SLIDER_MIN ? ((value - SLIDER_MIN) / (max - SLIDER_MIN)) * 100 : 0;
  return theme === 'dark'
    ? `linear-gradient(to right, #FF60B6 0%, #2960FD ${fillPct}%, #2D3748 ${fillPct}%, #2D3748 100%)`
    : `linear-gradient(to right, #FF60B6 0%, #2960FD ${fillPct}%, #E5E7EB ${fillPct}%, #E5E7EB 100%)`;
}

const CustomStep2c_Weightage = ({
  skills = [],
  weightages = {},
  difficulties = {},
  duration = 45,
  onWeightageChange,
  onDifficultyChange,
  onAutoDistribute,
  onSubStepNav,
}) => {
  const { theme } = useTheme();

  const totalWeightage = useMemo(
    () => Object.values(weightages).reduce((sum, w) => sum + Number(w || 0), 0),
    [weightages],
  );

  if (!skills.length) return null;

  const maxWeightage = calcMaxWeightage(skills.length);

  return (
    <div className="space-y-6 mb-10">
      <SubStepBreadcrumb activeIndex={2} onStepClick={onSubStepNav} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <span className="text-xl dark:text-white text-textPrimary">
            Assign weightage and difficulty for each focus area
          </span>
          <span className="text-xl text-red-500">*</span>
        </div>
        <button
          type="button"
          onClick={onAutoDistribute}
          className={`${theme !== 'dark' ? 'border-btn !text-textSecondary' : 'button'} flex items-center gap-1.5 text-sm dark:!text-white hover:opacity-80 transition-all duration-200`}
        >
          <span>
            <Sparkles className="w-3.5 h-3.5 dark:text-white text-textSecondary" />
          </span>
          <span>Auto-distribute</span>
        </button>
      </div>

      {/* Total weightage indicator */}
      <div className="flex items-center gap-2 text-sm">
        <span className="dark:text-gray-400 text-textSecondary">
          Total weightage:
        </span>
        <span
          className={`font-semibold ${totalWeightage === 100 ? 'text-green-500' : 'text-yellow-500'}`}
        >
          {totalWeightage}%
        </span>
        {totalWeightage !== 100 && (
          <span className="text-xs text-yellow-500">(must total 100%)</span>
        )}
      </div>

      {/* Skill cards */}
      <div className="space-y-4">
        {skills.map((skill) => {
          const w = weightages[skill] || 0;
          const maxTopics = calcMaxTopics(duration, w);

          return (
            <div
              key={skill}
              className="rounded-lg p-5 border dark:border-gray-700/50 border-gray-200"
            >
              {/* Skill name + topic preview chip */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium dark:text-white text-textPrimary">
                  {skill}
                </h3>
                <span
                  className="text-xs px-2.5 py-1 rounded-full dark:bg-purple-900/30 bg-purple-50
                  dark:text-purple-300 text-purple-700 border dark:border-purple-700/40 border-purple-200"
                >
                  → up to {maxTopics} topic{maxTopics !== 1 ? 's' : ''}
                </span>
              </div>

              <div className="flex items-start gap-6">
                {/* Weightage slider — 2/5 width */}
                <div className="w-2/5">
                  <label className="block text-sm dark:text-gray-300 text-textSecondary mb-3">
                    Skill Weightage
                  </label>
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <input
                        type="range"
                        min="10"
                        max={maxWeightage}
                        step="1"
                        value={w}
                        onChange={(e) =>
                          onWeightageChange(skill, e.target.value)
                        }
                        className="w-full h-2 accent-blue-600 rounded-full appearance-none focus:outline-none cursor-pointer relative"
                        style={{
                          background: getSliderBackground(
                            w,
                            maxWeightage,
                            theme,
                          ),
                        }}
                      />
                      <div className="flex justify-between text-xs text-gray-400 mt-2 px-1">
                        {[10, 20, 30, 40, 50, 60, 70, 80, 90, 100]
                          .filter((v) => v <= maxWeightage)
                          .map((v) => (
                            <span key={v}>{v}</span>
                          ))}
                      </div>
                    </div>
                    <div className="border dark:border-gray-700 border-gray-300 rounded-full px-4 py-2 text-center min-w-[60px]">
                      <span className="dark:text-gray-300 text-textSecondary text-sm">
                        {w}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* Difficulty buttons — 3/5 width */}
                <div className="w-3/5">
                  <label className="block text-sm dark:text-gray-300 text-textSecondary mb-3">
                    Difficulty Level
                  </label>
                  <div className="flex gap-2">
                    {DIFFICULTY_OPTIONS.map((option) => {
                      const isSelected =
                        (difficulties[skill] || 'medium') === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() =>
                            onDifficultyChange(skill, option.value)
                          }
                          className={`flex-1 p-3 rounded-lg border transition-all shadow-sm relative
                            ${isSelected ? 'border-purple-400' : 'dark:border-gray-700/50 border-gray-200 hover:border-purple-400'}`}
                          style={{
                            backgroundImage:
                              'linear-gradient(233.57deg, rgba(217, 217, 217, 0.05) -5.01%, rgba(115, 108, 207, 0.01) 100%)',
                          }}
                        >
                          <div className="text-sm dark:text-[#CCCCCC] text-textSecondary mb-0.5">
                            {option.label}
                          </div>
                          <div className="text-[10px] dark:text-[#CCCCCC] text-textSecondary opacity-80">
                            {option.description}
                          </div>
                          {isSelected && (
                            <div className="absolute top-1.5 right-1.5 h-4 w-4 rounded-full bg-gradient-to-r from-pink-500 to-blue-500 flex justify-center items-center">
                              <Check className="h-3 w-3 text-white" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CustomStep2c_Weightage;
